import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSocket, safeEmit } from "../../../../../api/socket";
import { useSelector } from "react-redux";
import ringSound from "./teams.mp3"; // ensure file exists
import backend from "../../../../../api";
import "./IncomingCallListener.css";

export default function IncomingCallListener() {
  const nav = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;

  const [incoming, setIncoming] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [callerName, setCallerName] = useState("Unknown");
  const ringRef = useRef(null);
  const guardRef = useRef(false);
  const unlockedRef = useRef(false);
  const popupSafetyTimerRef = useRef(null);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false); // New state

  // Unlock autoplay on first user interaction
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      const el = ringRef.current;
      if (!el) return;
      el.muted = true;
      Promise.resolve(el.play())
        .then(() => {
          el.pause();
          el.currentTime = 0;
          el.muted = false;
          unlockedRef.current = true;
          document.removeEventListener("click", unlock);
          document.removeEventListener("keydown", unlock);
        })
        .catch(() => {});
    };
    document.addEventListener("click", unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  // Request microphone permission early (on component mount)
  useEffect(() => {
    async function requestMicPermission() {
      try {
        // Check current permission status
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
        
        if (permissionStatus.state === 'granted') {
          setMicPermissionGranted(true);
          return;
        }

        if (permissionStatus.state === 'denied') {
          console.warn("[mic] Permission denied previously.");
          // Optionally show a message to user to enable in settings
          return;
        }

        // If 'prompt', request it now
        if (permissionStatus.state === 'prompt') {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Stop tracks immediately since we don't need the stream yet
          stream.getTracks().forEach(track => track.stop());
          setMicPermissionGranted(true);
        }
      } catch (error) {
        console.error("[mic request error]", error);
        // Handle error, e.g., show a UI message: "Microphone access is required for calls."
      }
    }

    if (navigator.permissions && navigator.mediaDevices) {
      requestMicPermission();
    }
  }, []); // Runs once on mount

  // Safety to prevent stuck modal/guard if caller leaves
  const startPopupSafetyTimer = () => {
    clearPopupSafetyTimer();
    popupSafetyTimerRef.current = setTimeout(() => {
      finish();
    }, 30000); // auto-clear after 30s
  };
  const clearPopupSafetyTimer = () => {
    if (popupSafetyTimerRef.current) {
      clearTimeout(popupSafetyTimerRef.current);
      popupSafetyTimerRef.current = null;
    }
  };

  const finish = () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);
    setIncoming(null);
    guardRef.current = false;
    clearPopupSafetyTimer();
  };

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !userId) return;

    const onOffer = async (data) => {
      // With server fix, payload includes { to, from, signal, callId }
      console.log("[socket] incoming call:offer", data);
      if (!data || data.to !== userId) return;
      if (guardRef.current) return;
      guardRef.current = true;

      let name = "Unknown";
      try {
        // Adjust to your actual user endpoint; fallback gracefully
        const res = await backend.get(`/users/${data.from}`);
        name = res?.data?.data?.name || data.from;
      } catch {
        name = data.from;
      }

      setCallerName(name);
      setIncoming({ from: data.from, to: userId, signal: data.signal, callId: data.callId });
      setShowPopup(true);
      ringRef.current?.play().catch(() => {});
      startPopupSafetyTimer();
    };

    socket.on("call:offer", onOffer);
    socket.on("call:end", finish);
    socket.on("call:decline", finish);

    return () => {
      socket.off("call:offer", onOffer);
      socket.off("call:end", finish);
      socket.off("call:decline", finish);
    };
  }, [userId]);

  const answerCall = async () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);
    // if (incoming) {
      localStorage.setItem("incomingCall", JSON.stringify(incoming));
      nav(`/call/${incoming.from}`);
      
    // }
    setIncoming(null);
    guardRef.current = false;
    clearPopupSafetyTimer();
  };

  const declineCall = async () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);
    try {
      if (incoming) {
        await safeEmit("call:decline", { to: incoming.from, from: userId, callId: incoming.callId });
      }
    } catch {}
    setIncoming(null);
    guardRef.current = false;
    clearPopupSafetyTimer();
  };

  return (
    <>
      <audio ref={ringRef} loop preload="auto" src={ringSound} />
      {showPopup && incoming && (
        <div className="icl-modal" role="dialog" aria-modal="true">
          <div className="icl-modal-content">
            <h3>Incoming Call</h3>
            <p>From: {callerName}</p>
            <div className="icl-actions">
              <button className="btn-brand" onClick={answerCall}>Accept</button>
              <button className="btn-light" onClick={declineCall}>Decline</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}